/**
 * Google Sheets public-URL intake (zero-auth, best-effort per R-3). We convert
 * a shared/edit/published URL into its CSV-export endpoint and fetch it. A
 * private or unreachable sheet produces one precise, actionable error — never a
 * stack trace or a generic failure (criterion 7). We never touch OAuth or a
 * private Sheets API; if the public path fails, the message tells the user
 * exactly how to make it work or to fall back to a CSV download.
 */

/** The single actionable message shown for any non-public / unreachable sheet. */
export const SHEET_ACCESS_MESSAGE =
  "This Google Sheet isn't publicly readable. In Google Sheets open File → Share and set it to " +
  "“Anyone with the link”, or File → Share → Publish to web — or just download it as CSV and drop it here.";

export class SheetAccessError extends Error {
  constructor(message: string = SHEET_ACCESS_MESSAGE) {
    super(message);
    this.name = "SheetAccessError";
  }
}

const DOC_ID_RE = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
const PUB_ID_RE = /\/spreadsheets\/d\/e\/([a-zA-Z0-9-_]+)/;
const GID_RE = /[#&?]gid=([0-9]+)/;

/**
 * Client-safe predicate: does `url` look like a recognizable Google Sheets link
 * (normal `/d/<id>` share or `/d/e/<pubid>` published)? Pure — the SheetPanel
 * uses it for live validation before enabling "Fetch", without throwing.
 */
export function isGoogleSheetUrl(url: string): boolean {
  const u = url.trim();
  if (u === "") return false;
  return PUB_ID_RE.test(u) || DOC_ID_RE.test(u);
}

/**
 * Convert a Google Sheets URL to its CSV-export URL. Handles the normal
 * `/d/<id>/edit` share link and the `/d/e/<pubid>/pubhtml` published link.
 * Throws SheetAccessError if the URL isn't a recognizable Google Sheets link.
 */
export function sheetsCsvUrl(url: string): string {
  const gid = url.match(GID_RE)?.[1];

  const pub = url.match(PUB_ID_RE)?.[1];
  if (pub) {
    const base = `https://docs.google.com/spreadsheets/d/e/${pub}/pub?output=csv`;
    return gid ? `${base}&gid=${gid}` : base;
  }

  const id = url.match(DOC_ID_RE)?.[1];
  if (id) {
    const base = `https://docs.google.com/spreadsheets/d/${id}/export?format=csv`;
    return gid ? `${base}&gid=${gid}` : base;
  }

  throw new SheetAccessError(
    "That doesn't look like a Google Sheets link. Paste the URL from your browser's address bar while the sheet is open, or download it as CSV and drop it here.",
  );
}

const looksLikeHtml = (body: string): boolean =>
  /^\s*<(?:!doctype|html)/i.test(body.slice(0, 200));

/**
 * Fetch a public Google Sheet as CSV text. `fetchImpl` is injectable for tests.
 * A private/unreachable sheet returns an HTML login/error page or a non-OK
 * status; both map to the one precise SheetAccessError.
 */
export async function fetchSheetCsv(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const csvUrl = sheetsCsvUrl(url);
  let res: Response;
  try {
    res = await fetchImpl(csvUrl, { redirect: "follow" });
  } catch {
    throw new SheetAccessError();
  }

  if (!res.ok) throw new SheetAccessError();

  const contentType = res.headers.get("content-type") ?? "";
  const body = await res.text();
  // Google serves a redirect to an HTML login/consent page for private sheets;
  // a real export is text/csv. Guard on both content-type and body shape.
  if (contentType.includes("text/html") || looksLikeHtml(body)) throw new SheetAccessError();
  if (body.trim() === "") throw new SheetAccessError();

  return body;
}
